import { useSetAtom } from "jotai";
import { useEffect, useRef } from "react";
import type { QaPage } from "@CommonApi/merchflows/_merchflowId_/qa";
import { Color, Flex, Text } from "src/elements";
import { useTranslation } from "src/i18n";
import { styledSafe } from "src/utils/styledComponent";
import { zIndexLevel } from "src/utils/zIndexLevels";
import { O_ASK_DRAWER_PX, O_ASK_NARROW_PX, oAskDockedAtom } from "../store/oAskDock";
import { useOQa } from "../store/useOQa";
import { OAskPanel } from "./OAskPanel";
import { LinkButton, OButton, OMark } from "./OParts";

// Beside the page on a wide window (the page leaves its width free), over it on a narrow one.
// Under modals: a place O opens (the rules modal) shows above it.
const Drawer = styledSafe("aside")`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: ${zIndexLevel.L9};
  display: flex;
  flex-direction: column;
  width: ${O_ASK_DRAWER_PX}px;
  background: ${Color.white};
  border-left: 1px solid ${Color.lightGrayHover};
  box-shadow: -4px 0 16px rgba(0, 0, 0, 0.08);
  animation: o-ask-in 180ms ease-out;

  @keyframes o-ask-in {
    from {
      transform: translateX(100%);
    }
    to {
      transform: none;
    }
  }

  // Narrow windows: the whole width, so the page never shows a sliver beside it.
  @media (max-width: ${O_ASK_NARROW_PX - 1}px) {
    left: 0;
    width: auto;
    border-left: none;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

interface Props {
  merchflowId: number;
  page: QaPage;
}

/** The button by O's mark, and the side drawer it opens. One per merchflow page, in the header. */
export const OAskEntry = ({ merchflowId, page }: Props) => {
  const { t } = useTranslation("common");
  const o = useOQa(merchflowId);
  const entry = useRef<HTMLButtonElement>(null);
  const drawer = useRef<HTMLElement>(null);
  const setDocked = useSetAtom(oAskDockedAtom);

  useEffect(() => {
    setDocked(o.isOpen);
    return () => setDocked(false);
  }, [o.isOpen]);

  const close = () => {
    o.setOpen(false);
    entry.current?.focus({ preventScroll: true });
  };

  // Esc closes the drawer while focus is in it or on its button, or nowhere. Focus in a modal
  // or on the page is left alone, so their own Esc (close the rules, drop a selection) still works.
  useEffect(() => {
    if (!o.isOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      const at = document.activeElement;
      const isOurs =
        !at ||
        at === document.body ||
        at === entry.current ||
        (drawer.current?.contains(at) ?? false);
      if (isOurs) close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [o.isOpen, merchflowId]);

  return (
    <>
      <OButton
        ref={entry}
        type="button"
        aria-expanded={o.isOpen}
        aria-controls="o-ask-drawer"
        data-testid="o-ask-entry"
        onClick={() => o.setOpen(!o.isOpen)}
      >
        <Flex gap="6px" align="center">
          <OMark size={18} />
          {t("configCopilot.askO")}
        </Flex>
      </OButton>

      {o.isOpen && (
        <Drawer
          ref={drawer}
          id="o-ask-drawer"
          role="dialog"
          aria-modal={false}
          aria-label={t("configCopilot.askO")}
          data-testid="o-ask-drawer"
        >
          <Flex justify="between" align="center" padding="12px 16px">
            <Flex gap="8px" align="center">
              <OMark size={24} />
              <Text variant="h5" color={Color.primaryActive}>
                {t("configCopilot.askO")}
              </Text>
            </Flex>
            <LinkButton type="button" data-testid="o-ask-close" onClick={close}>
              {t("configCopilot.qa.close")}
            </LinkButton>
          </Flex>

          <OAskPanel merchflowId={merchflowId} page={page} onLeave={() => o.setOpen(false)} />
        </Drawer>
      )}
    </>
  );
};
